import { useEffect, useMemo, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { StatusBadge } from "./_ui";

const money = (cents) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const PAYOUT_BADGE = { ready: "ready", in_review: "in-review", needs_info: "info-requested", in_progress: "info-requested", not_started: "pending" };
export const PAYOUT_LABEL = { ready: "Payouts ready", in_review: "Stripe verifying", needs_info: "Needs info", in_progress: "Onboarding started", not_started: "Not onboarded" };

/**
 * NewGrantDialog — admin schedules a grant against an approved campaign.
 * Amount is entered in dollars; we send integer cents.
 */
export const NewGrantDialog = ({ open, onOpenChange, options, onCreate }) => {
  const [campaignId, setCampaignId] = useState("");
  const [dollars, setDollars] = useState("");
  const [memo, setMemo] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) { setCampaignId(""); setDollars(""); setMemo(""); setBusy(false); setError(""); }
  }, [open]);

  const selected = useMemo(() => options.find((o) => o.campaign_id === campaignId), [options, campaignId]);
  const cents = Math.round(parseFloat(dollars || "0") * 100);
  const valid = selected && cents > 0 && cents <= 1_000_000;

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await onCreate({ campaign_id: campaignId, amount_cents: cents, memo: memo.trim() || null });
      onOpenChange(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "That didn't go through. Try again in a moment.");
      setBusy(false);
    }
  };

  const inputCls = "mt-2 w-full rounded-md border border-border bg-bone px-4 py-3 text-sm text-ink placeholder:text-ink/30 focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-grant-dialog" className="max-w-lg border-border bg-sand p-0 gap-0 sm:rounded-lg">
        <div className="relative overflow-hidden bg-beige px-8 py-7">
          <span className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-grass/30 blur-3xl" />
          <DialogTitle className="relative font-serif text-3xl font-normal leading-[1.05] text-ink">Schedule a grant</DialogTitle>
          <DialogDescription className="relative mt-2 text-sm leading-relaxed text-olive">
            Funds move from My Rich Aunt's Stripe balance to the student's connected account when you authorize the weekly batch. Nothing is sent now.
          </DialogDescription>
        </div>

        <div className="space-y-5 px-8 py-6">
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">Campaign</span>
            <select data-testid="new-grant-campaign" value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className={inputCls}>
              <option value="">Choose an approved campaign…</option>
              {options.map((o) => (
                <option key={o.campaign_id} value={o.campaign_id}>
                  {o.student_name} — {o.title} ({o.school || o.status})
                </option>
              ))}
            </select>
          </label>

          {selected && (
            <div className="rounded-md border border-border bg-bone px-4 py-3 text-sm" data-testid="new-grant-campaign-summary">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-ink">{selected.student_email}</span>
                <StatusBadge status={PAYOUT_BADGE[selected.payout_state] || "pending"} />
              </div>
              <p className="mt-1 text-xs text-olive">
                {PAYOUT_LABEL[selected.payout_state]} · raised {money(selected.raised_cents)} of {money(selected.goal_cents)} · granted so far {money(selected.granted_cents)}
              </p>
              {!selected.payouts_ready && (
                <p className="mt-2 flex items-start gap-1.5 text-xs text-tangerine">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                  You can schedule now, but the transfer will be skipped until this student finishes Stripe onboarding.
                </p>
              )}
            </div>
          )}

          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">Amount (USD)</span>
            <input data-testid="new-grant-amount" type="number" min="1" max="10000" step="0.01" inputMode="decimal" value={dollars} onChange={(e) => setDollars(e.target.value)} placeholder="500.00" className={inputCls} />
          </label>

          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">Memo (the student sees this)</span>
            <textarea data-testid="new-grant-memo" rows={2} maxLength={280} value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Study-abroad housing deposit, spring term." className={inputCls} />
          </label>

          {error && (
            <p role="alert" data-testid="new-grant-error" className="flex items-start gap-2 rounded-md bg-scarlet/8 px-3 py-2 text-sm text-scarlet">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} /> {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={() => onOpenChange(false)} disabled={busy} data-testid="new-grant-cancel" className="rounded-full border border-border bg-bone px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-ink disabled:opacity-60">
              Cancel
            </button>
            <button type="button" onClick={submit} disabled={!valid || busy} data-testid="new-grant-submit" className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Schedule {valid ? money(cents) : "grant"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
